import { getLocale, localizeUrl } from "@/paraglide/runtime";

const getBaseUrl = () => {
	if (typeof window !== "undefined" && window.location?.origin) {
		return window.location.origin;
	}
	return import.meta.env.VITE_BASE_URL || "https://studyos.pro";
};

export const SITE_NAME = "StudyOS";
export const DEFAULT_TITLE = "StudyOS | AI-Powered Interactive Learning";
export const DEFAULT_DESCRIPTION =
	"StudyOS turns prompts and source material into interactive study sessions, quizzes, flashcards, and guided AI learning experiences.";
export const DEFAULT_KEYWORDS = [
	"StudyOS",
	"AI learning platform",
	"interactive study sessions",
	"AI education",
	"flashcards",
	"quizzes",
	"study assistant",
];
export const DEFAULT_OG_IMAGE_PATH = "/studyos-text-icon.png";

type StructuredData = Record<string, unknown>;
type JsonLdMetaTag = { "script:ld+json": StructuredData };

type SeoInput = {
	title?: string;
	description?: string;
	pathname?: string;
	image?: string;
	keywords?: string | string[];
	noIndex?: boolean;
	ogType?: "website" | "article";
	structuredData?: StructuredData | StructuredData[];
};

type SeoHead = {
	meta: React.JSX.IntrinsicElements["meta"][];
	links: React.JSX.IntrinsicElements["link"][];
};

export function toAbsoluteUrl(pathname: string | URL) {
	if (pathname instanceof URL) {
		return pathname.toString();
	}

	if (/^https?:\/\//.test(pathname)) {
		return pathname;
	}

	return new URL(pathname, getBaseUrl()).toString();
}

function normalizePathname(pathname: string) {
	return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

export function buildCanonicalUrl(pathname = "/") {
	const absoluteUrl = new URL(normalizePathname(pathname), getBaseUrl());
	const localizedUrl = localizeUrl(absoluteUrl, {
		locale: getLocale(),
	});

	return localizedUrl.href;
}

function toKeywordContent(keywords?: string | string[]) {
	if (!keywords) {
		return DEFAULT_KEYWORDS.join(", ");
	}

	return Array.isArray(keywords) ? keywords.join(", ") : keywords;
}

function normalizeStructuredData(input?: StructuredData | StructuredData[]) {
	if (!input) return [];
	return Array.isArray(input) ? input : [input];
}

export function seo({
	title = DEFAULT_TITLE,
	description = DEFAULT_DESCRIPTION,
	pathname = "/",
	image = DEFAULT_OG_IMAGE_PATH,
	keywords,
	noIndex = false,
	ogType = "website",
	structuredData,
}: SeoInput): SeoHead {
	const canonicalUrl = buildCanonicalUrl(pathname);
	const imageUrl = toAbsoluteUrl(image);
	const robots = noIndex
		? "noindex, nofollow, noarchive"
		: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

	return {
		meta: [
			{ title },
			{ name: "description", content: description },
			{ name: "keywords", content: toKeywordContent(keywords) },
			{ name: "robots", content: robots },
			{ name: "application-name", content: SITE_NAME },
			{ name: "apple-mobile-web-app-title", content: SITE_NAME },
			{ name: "twitter:card", content: "summary_large_image" },
			{ name: "twitter:title", content: title },
			{ name: "twitter:description", content: description },
			{ name: "twitter:image", content: imageUrl },
			{ property: "og:type", content: ogType },
			{ property: "og:site_name", content: SITE_NAME },
			{ property: "og:title", content: title },
			{ property: "og:description", content: description },
			{ property: "og:url", content: canonicalUrl },
			{ property: "og:image", content: imageUrl },
			...normalizeStructuredData(structuredData).map(
				(jsonLd) =>
					({
						"script:ld+json": jsonLd,
					}) as JsonLdMetaTag as React.JSX.IntrinsicElements["meta"],
			),
		],
		links: [{ rel: "canonical", href: canonicalUrl }],
	};
}

export function noIndexSeo(input: Omit<SeoInput, "noIndex">) {
	return seo({ ...input, noIndex: true });
}

export function createWebSiteJsonLd() {
	return {
		"@context": "https://schema.org",
		"@type": "WebSite",
		name: SITE_NAME,
		url: toAbsoluteUrl("/"),
		description: DEFAULT_DESCRIPTION,
		inLanguage: getLocale(),
	};
}

export function createOrganizationJsonLd() {
	return {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: SITE_NAME,
		url: toAbsoluteUrl("/"),
		logo: toAbsoluteUrl(DEFAULT_OG_IMAGE_PATH),
	};
}

export function createWebPageJsonLd({
	title,
	description,
	pathname,
}: {
	title: string;
	description: string;
	pathname: string;
}) {
	return {
		"@context": "https://schema.org",
		"@type": "WebPage",
		name: title,
		description,
		url: buildCanonicalUrl(pathname),
		isPartOf: {
			"@type": "WebSite",
			name: SITE_NAME,
			url: toAbsoluteUrl("/"),
		},
		inLanguage: getLocale(),
	};
}
